import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";
import { TaskService } from "../../services/task/taskService";
import { useToast } from "../../components/common/ToastManager/ToastContext";
import Loader from "../../components/common/Loader/Loader";
import "./Catalog.css";

const OUTPUT_FIELDS: { key: string; label: string }[] = [
  { key: "product_number",          label: "Product Number" },
  { key: "title",                   label: "Title" },
  { key: "description",             label: "Description" },
  { key: "seo_title",               label: "SEO Title" },
  { key: "seo_description",         label: "SEO Description" },
  { key: "seo_keywords",            label: "SEO Keywords" },
  { key: "highlights",              label: "Highlights" },
  { key: "attributes",              label: "Attributes" },
  { key: "faq",                     label: "FAQ" },
  { key: "ai_summary",              label: "AI Summary" },
  { key: "differentiator",          label: "Differentiator" },
  { key: "structured_product_data", label: "Structured Product Data" },
];

const DEFAULT_MAPPINGS: Record<string, string> = {
  product_number: "Product Number",
  title: "Product Title",
  description: "CurrentDefaultContent",
  seo_title: "Page Title",
  seo_description: "Meta Description",
  seo_keywords: "Meta Keywords",
  highlights: "Highlights",
  attributes: "Attributes",
  faq: "FAQ",
  ai_summary: "AI Summary",
  differentiator: "Differentiator",
  structured_product_data: "Structured Product Data",
};

const DEFAULT_ENABLED: Record<string, boolean> = Object.fromEntries(
  OUTPUT_FIELDS.map((f) => [
    f.key,
    ["product_number", "title", "description", "seo_title", "seo_description", "seo_keywords"].includes(f.key),
  ])
);

interface Props {
  isOpen: boolean;
  onClose: () => void;
}

const FieldMappingModal: React.FC<Props> = ({ isOpen, onClose }) => {
  const { showToast } = useToast();
  const [fieldMap, setFieldMap] = useState<Record<string, string>>({ ...DEFAULT_MAPPINGS });
  const [enabledFields, setEnabledFields] = useState<Record<string, boolean>>({ ...DEFAULT_ENABLED });
  const [isSaving, setIsSaving] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (!isOpen) return;
    setErrors({});
    setEnabledFields({ ...DEFAULT_ENABLED });
    setIsLoading(true);
    TaskService.getOptimizelyFieldMappings()
      .then((res) => {
        const saved = res.data?.fieldMapping?.mappings as any;
        if (saved && typeof saved === "object") {
          const flat: Record<string, string> = saved.output ?? saved;
          const displayMap = { ...DEFAULT_MAPPINGS };
          const newEnabled: Record<string, boolean> = { ...DEFAULT_ENABLED };
          OUTPUT_FIELDS.forEach((f) => {
            if (flat[f.key] === "") {
              newEnabled[f.key] = false;
            } else if (flat[f.key] !== undefined) {
              displayMap[f.key] = flat[f.key];
              newEnabled[f.key] = true;
            }
          });
          setFieldMap(displayMap);
          setEnabledFields(newEnabled);
        } else {
          setFieldMap({ ...DEFAULT_MAPPINGS });
        }
      })
      .catch(() => setFieldMap({ ...DEFAULT_MAPPINGS }))
      .finally(() => setIsLoading(false));
  }, [isOpen]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    if (isOpen) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, onClose]);

  const computeDuplicateErrors = (
    map: Record<string, string>,
    enabled: Record<string, boolean>,
  ): Record<string, string> => {
    const errs: Record<string, string> = {};
    const groups: Record<string, string[]> = {};
    OUTPUT_FIELDS.forEach((f) => {
      if (!enabled[f.key]) return;
      const v = map[f.key]?.trim().toLowerCase();
      if (!v) return;
      if (!groups[v]) groups[v] = [];
      groups[v].push(f.key);
    });
    Object.values(groups).forEach((keys) => {
      if (keys.length > 1) keys.forEach((k) => (errs[k] = "Duplicate name"));
    });
    return errs;
  };

  const validate = (): boolean => {
    const errs = computeDuplicateErrors(fieldMap, enabledFields);
    if (!fieldMap["product_number"]?.trim()) errs["product_number"] = "Required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    setIsSaving(true);
    try {
      const saveMap: Record<string, string> = {};
      OUTPUT_FIELDS.forEach((f) => {
        saveMap[f.key] = enabledFields[f.key]
          ? (fieldMap[f.key]?.trim() || DEFAULT_MAPPINGS[f.key])
          : "";
      });
      saveMap["product_number"] = fieldMap["product_number"]?.trim() || DEFAULT_MAPPINGS["product_number"];
      await TaskService.updateOptimizelyFieldMappings(saveMap);
      showToast("Field mappings saved successfully!", "success");
      onClose();
    } catch {
      showToast("Failed to save field mappings.", "error");
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen) return null;

  return ReactDOM.createPortal(
    <div className="modal-container" onClick={onClose}>
      <div className="fm-modal" onClick={(e) => e.stopPropagation()}>

        <div className="fm-header">
          <div>
            <h2 className="fm-title">Field Mapping</h2>
            <p className="fm-subtitle">Customize column names used in the exported Excel file.</p>
          </div>
          <img
            src="/images/cross.svg"
            alt="Close"
            style={{ cursor: "pointer", width: "22px", height: "22px" }}
            onClick={onClose}
          />
        </div>

        {isLoading ? (
          <div style={{ padding: "48px", display: "flex", justifyContent: "center", flex: 1 }}>
            <Loader inline={true} circular={true} />
          </div>
        ) : (
          <div className="fm-table-wrap">
            <div className="fm-table-header">
              <span>FIELD</span>
              <span></span>
              <span>EXPORTED AS</span>
              <span style={{ textAlign: "center" }}>INCLUDE</span>
            </div>
            <div className="fm-table-body">
              {OUTPUT_FIELDS.map((f) => (
                <div className="fm-row" key={f.key}>
                  <span className="fm-field-label">
                    {f.label}
                    {f.key === "product_number" && <span style={{ color: "#dc2626" }}> *</span>}
                  </span>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                    <path d="M5 12h14M13 6l6 6-6 6" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  <div>
                    <input
                      className={`fm-input${errors[f.key] ? " fm-input-error" : ""}`}
                      type="text"
                      placeholder={`e.g. ${DEFAULT_MAPPINGS[f.key]}`}
                      value={fieldMap[f.key] || ""}
                      disabled={!enabledFields[f.key]}
                      onChange={(e) => {
                        const nextMap = { ...fieldMap, [f.key]: e.target.value };
                        setFieldMap(nextMap);
                        setErrors(computeDuplicateErrors(nextMap, enabledFields));
                      }}
                    />
                    {errors[f.key] && <span className="fm-error">{errors[f.key]}</span>}
                  </div>
                  <div style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
                    <input
                      type="checkbox"
                      className="fm-field-toggle"
                      checked={enabledFields[f.key]}
                      disabled={f.key === "product_number"}
                      onChange={(e) => {
                        const nextEnabled = { ...enabledFields, [f.key]: e.target.checked };
                        setEnabledFields(nextEnabled);
                        setErrors(computeDuplicateErrors(fieldMap, nextEnabled));
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="fm-footer">
          <button className="cancel-button" onClick={onClose} disabled={isSaving}>Cancel</button>
          <button
            className="submit-button"
            onClick={handleSave}
            disabled={isSaving || isLoading || Object.keys(errors).length > 0}
          >
            {isSaving ? "Saving..." : "Save Mappings"}
          </button>
        </div>

      </div>
    </div>,
    document.body
  );
};

export default FieldMappingModal;
